import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import { CheckCircle, RouteIcon as Road, Shield, Zap, Wrench } from "lucide-react"

const RoadMaintenancePage: React.FC = () => {
  const applications = [
    {
      title: "Street Sweeping & Cleaning",
      description: "Comprehensive street cleaning for urban and suburban environments",
      image: "https://placehold.co/600x400?text=Street+Sweeping",
      attachments: ["Front Sweeper", "High Volume Sweeper", "Street Cleaner", "City Cleaner"],
    },
    {
      title: "Surface Preparation & Repair",
      description: "Road surface preparation, patching, and maintenance operations",
      image: "https://placehold.co/600x400?text=Surface+Repair",
      attachments: ["Patch Planer", "Surface Cleaner", "High Pressure Washer", "Line Remover"],
    },
    {
      title: "Infrastructure Maintenance",
      description: "Maintenance of drainage systems, manholes, and road infrastructure",
      image: "https://placehold.co/600x400?text=Infrastructure",
      attachments: ["Gully Cleaner", "Sewer Cleaner", "Manhole Cutter", "High Tip Bucket"],
    },
    {
      title: "Highway & Arterial Roads",
      description: "Heavy-duty maintenance for highways and major arterial roads",
      image: "https://placehold.co/600x400?text=Highway+Maintenance",
      attachments: ["Heavy Duty Sweeper", "Barrier Cleaner", "Surface Cleaner", "Safety Sign"],
    },
  ]

  const attachments = [
    {
      name: "Front Sweeper",
      description: "Efficient street and pathway cleaning",
      specs: "Variable brush pressure and speed control",
    },
    {
      name: "High Volume Sweeper",
      description: "Heavy-duty sweeping for large areas",
      specs: "High-capacity debris collection",
    },
    {
      name: "Street Cleaner",
      description: "Comprehensive street cleaning solution",
      specs: "Integrated water system for dust suppression",
    },
    {
      name: "Surface Cleaner",
      description: "High-pressure surface cleaning",
      specs: "Adjustable pressure settings",
    },
    {
      name: "High Pressure Washer",
      description: "Powerful washing for tough cleaning jobs",
      specs: "Up to 3000 PSI cleaning power",
    },
    {
      name: "Patch Planer",
      description: "Road surface preparation and repair",
      specs: "Precision depth control",
    },
    {
      name: "Gully and Sewer Cleaner",
      description: "Drainage system maintenance",
      specs: "High-suction debris removal",
    },
    {
      name: "Line Remover",
      description: "Road marking removal",
      specs: "Clean removal without surface damage",
    },
  ]

  const benefits = [
    {
      icon: Shield,
      title: "Enhanced Safety",
      description: "Maintain safe road conditions with professional cleaning and repair",
    },
    {
      icon: Zap,
      title: "Efficient Operations",
      description: "Complete multiple road maintenance tasks with one versatile machine",
    },
    {
      icon: Wrench,
      title: "Precision Work",
      description: "Accurate surface preparation and repair with specialized attachments",
    },
    {
      icon: Road,
      title: "Extended Road Life",
      description: "Proactive maintenance extends the lifespan of road infrastructure",
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Road Maintenance Applications
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional
              <br />
              <span className="text-blue-400">Road Maintenance</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Comprehensive road maintenance solutions for highways, streets, and infrastructure. From surface cleaning
              to repair preparation, maintain safe and efficient roadways year-round.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Get Road Maintenance Quote
              </Link>
              <Link
                to="/attachments"
                className="border border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Attachments
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Road Maintenance Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized solutions for comprehensive road and infrastructure maintenance across all types of roadways
                and conditions.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {applications.map((app, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <img src={app.image || "/placeholder.svg"} alt={app.title} className="w-full h-64 object-cover" />
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{app.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Recommended Attachments:</h4>
                      <div className="flex flex-wrap gap-2">
                        {app.attachments.map((attachment, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                          >
                            {attachment}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Multihog for Road Maintenance?</h2>
              <p className="text-lg text-gray-600">
                Experience the advantages of professional road maintenance equipment
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Attachments Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Road Maintenance Attachments</h2>
              <p className="text-lg text-gray-600">
                Specialized attachments designed for professional road maintenance applications
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {attachments.map((attachment, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{attachment.name}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{attachment.description}</p>
                  <p className="text-blue-600 text-xs font-medium">{attachment.specs}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Real-World Performance</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <blockquote className="text-lg text-gray-600 italic mb-6">
                "The Multihog MX with street sweeper attachment has revolutionized our road maintenance operations. We
                can now complete in one day what used to take three days with our old equipment."
              </blockquote>
              <div className="text-gray-900 font-semibold">City of Melbourne Public Works Department</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Upgrade Your Road Maintenance?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Transform your road maintenance operations with professional-grade Multihog solutions. Our experts are
              ready to help you choose the perfect configuration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Schedule Consultation
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Compatible Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default RoadMaintenancePage
